import { useState } from 'react';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Dialog, Input, Pagination } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { date, humanise } from '../../lib/format.js';

const SEVERITY_TONE = { critical: 'danger', warning: 'warning', notice: 'info', info: 'neutral' };

/**
 * Every recorded action, newest first: what admins changed, and the account
 * events users triggered themselves (such as deleting their account).
 *
 * Search matches the action name — "report", "refund", "config", "deleted".
 * A row opens the before and after it recorded.
 */
export default function AuditLogPage() {
  const [open, setOpen] = useState(null);

  const list = useListQuery({ key: 'admin-audit-logs', fetcher: api.audit.list, searchable: true, pageSize: 30 });

  const columns = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="table__muted nowrap">{date(row.createdAt, { withTime: true })}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => <span className="mono">{row.action}</span>,
    },
    {
      key: 'who',
      header: 'By',
      render: (row) => (
        <div>
          <div>{row.actorEmail || humanise(row.actorType)}</div>
          {row.actorEmail ? null : <div className="table__muted mono">{row.actor ? String(row.actor) : ''}</div>}
        </div>
      ),
    },
    {
      key: 'entity',
      header: 'On',
      render: (row) => (
        <div>
          <div>{row.entityType}</div>
          <div className="table__muted mono">{row.entityId ? String(row.entityId) : ''}</div>
        </div>
      ),
    },
    {
      key: 'severity',
      header: '',
      render: (row) => (row.severity ? <Badge tone={SEVERITY_TONE[row.severity] || 'neutral'} label={row.severity} /> : null),
    },
  ];

  return (
    <Page title="Audit log" subtitle="Who did what, and when. Kept for every admin action and account deletion.">
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__search">
              <Input
                placeholder="Search by action, e.g. refund, report, config"
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
                aria-label="Search the audit log"
              />
            </div>
          </div>
        </div>
        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          onRowClick={setOpen}
          empty={{ title: 'Nothing recorded', body: 'No entries match this search.' }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>

      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} title={open?.action || ''} size="wide">
        {open ? (
          <div className="stack">
            {open.reason ? <p>{open.reason}</p> : null}
            {open.before ? (
              <div>
                <strong>Before</strong>
                <pre className="code-block">{JSON.stringify(open.before, null, 2)}</pre>
              </div>
            ) : null}
            {open.after ? (
              <div>
                <strong>After</strong>
                <pre className="code-block">{JSON.stringify(open.after, null, 2)}</pre>
              </div>
            ) : null}
            {!open.before && !open.after ? <p className="muted">No detail was recorded for this action.</p> : null}
          </div>
        ) : null}
      </Dialog>
    </Page>
  );
}
